import { logReportDownload } from "../../util/auditLogger";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import Context from "../../Context";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { useToast, Spinner } from "../../components/ui";
import * as xlsx from "xlsx";
import {
  FiUsers,
  FiSearch,
  FiCopy,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMessageSquare,
  FiSmartphone,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
} from "react-icons/fi";
import { RiFileExcel2Line } from "react-icons/ri";

const formatPhoneDisplay = (phone) => {
  if (!phone) return "-";
  const clean = String(phone).trim();
  if (clean.length === 10 && clean.startsWith("0")) {
    return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
  }
  return clean;
};

const formatPhoneForExport = (phone) => {
  if (!phone) return "";
  const clean = String(phone).trim();
  if (clean.startsWith("0")) {
    return `66${clean.slice(1)}`;
  }
  return clean;
};

export const RoundDateBuyerList = () => {
  const { callApi, configs } = Context();
  const { toast } = useToast();

  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Mode: 'round' (รายงวด) | 'year' (ทั้งปี)
  const [mode, setMode] = useState("round");
  const [year, setYear] = useState("");

  const years = useMemo(
    () =>
      [...new Set((configs?.roundDates || []).map((d) => d.slice(0, 4)))]
        .sort()
        .reverse(),
    [configs],
  );
  const isYear = mode === "year";
  const label = isYear ? `ปี_${year}` : roundDate;

  // Filters & Search
  const [notifyFilter, setNotifyFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Sorting
  const [sortBy, setSortBy] = useState("size"); // 'size' | 'name' | 'userId'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Copied state for visual feedback
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Update roundDate when configs is loaded
  useEffect(() => {
    if (configs?.roundDate && !roundDate) {
      setRoundDate(configs.roundDate);
    }
  }, [configs, roundDate]);

  useEffect(() => {
    if (!year && years.length) setYear(years[0]);
  }, [years, year]);

  // Handle Search API (เฉพาะโหมดรายงวด)
  const handleSearch = async () => {
    if (!roundDate) {
      toast({
        variant: "destructive",
        title: "กรุณาเลือกงวด",
        description: "โปรดเลือกงวดที่ต้องการตรวจสอบก่อนกดค้นหา",
      });
      return;
    }

    setLoading(true);
    setCurrentPage(1);

    try {
      const result = await callApi("orders-getRounddateBuyer", { roundDate });

      if (result?.success && result?.data) {
        const list = Object.values(result.data);
        setRawData(list);
        setHasSearched(true);
        toast({
          title: "ดึงข้อมูลสำเร็จ",
          description: `พบผู้ซื้อทั้งหมด ${list.length.toLocaleString()} คนในงวด ${roundDate}`,
        });
      } else {
        setRawData([]);
        setHasSearched(true);
        toast({
          variant: "destructive",
          title: "ไม่พบข้อมูล",
          description: result?.message || "ไม่พบข้อมูลผู้ซื้อในงวดที่เลือก",
        });
      }
    } catch (error) {
      console.error("orders-getRounddateBuyer error:", error);
      toast({
        variant: "destructive",
        title: "เกิดข้อผิดพลาด",
        description: error.message || "ไม่สามารถดึงข้อมูลผู้ซื้อได้",
      });
    } finally {
      setLoading(false);
    }
  };

  // KPIs / Summaries
  const stats = useMemo(() => {
    const totalBuyers = rawData.length;
    let totalTickets = 0;
    let smsCount = 0;
    let lineCount = 0;

    rawData.forEach((u) => {
      totalTickets += Number(u.size) || 0;
      const nt = String(u.notifyType || "").toUpperCase();
      if (nt === "SMS") smsCount += 1;
      else if (nt === "LINE") lineCount += 1;
    });

    return { totalBuyers, totalTickets, smsCount, lineCount };
  }, [rawData]);

  // Filtered & Sorted Data
  const filteredData = useMemo(() => {
    let list = [...rawData];

    // Filter by Notify Type
    if (notifyFilter === "sms") {
      list = list.filter(
        (u) => String(u.notifyType || "").toUpperCase() === "SMS",
      );
    } else if (notifyFilter === "line") {
      list = list.filter(
        (u) => String(u.notifyType || "").toUpperCase() === "LINE",
      );
    }

    // Filter by Search Term
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter((u) => {
        const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
        const phone = String(u.phone || "").toLowerCase();
        const userId = String(u.userId || "").toLowerCase();
        const lineId = String(u.lineId || "").toLowerCase();
        return (
          fullName.includes(q) ||
          phone.includes(q) ||
          userId.includes(q) ||
          lineId.includes(q)
        );
      });
    }

    // Sort
    list.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "size") {
        comparison = (Number(b.size) || 0) - (Number(a.size) || 0);
      } else if (sortBy === "name") {
        const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim();
        const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim();
        comparison = nameA.localeCompare(nameB, "th");
      } else if (sortBy === "userId") {
        comparison = String(a.userId || "").localeCompare(String(b.userId || ""));
      }

      return sortOrder === "asc" ? -comparison : comparison;
    });

    return list;
  }, [rawData, notifyFilter, searchTerm, sortBy, sortOrder]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [notifyFilter, searchTerm, pageSize]);

  // Pagination Calculation
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Toggle Sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder(column === "name" || column === "userId" ? "asc" : "desc");
    }
  };

  // Export Excel (.xlsx)
  const handleExportExcel = useCallback(async () => {
    // โหมดทั้งปี: ยิงให้ Backend (Cloud Functions) สร้าง Excel และส่ง Base64 กลับมา ป้องกัน Browser Hang
    if (isYear) {
      if (!year) {
        toast({ variant: "destructive", title: "กรุณาเลือกปี" });
        return;
      }

      setExporting(true);
      try {
        const result = await callApi("orders-exportYearBuyers", { year });
        if (result?.success && result?.data?.base64) {
          const byteCharacters = atob(result.data.base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = result.data.filename || `รายงานผู้ซื้อรายปี_${year}.xlsx`;
          document.body.appendChild(link);
          logReportDownload();
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 60000);

          toast({
            title: "ดาวน์โหลดไฟล์ Excel สำเร็จ",
            description: `ดาวน์โหลดรายชื่อผู้ซื้อปี ${year} จำนวน ${(result.data.totalBuyers || 0).toLocaleString()} คนเรียบร้อยแล้ว`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "สร้างไฟล์ไม่สำเร็จ",
            description: result?.message || "ไม่สามารถดึงข้อมูลรายงานรายปีได้",
          });
        }
      } catch (err) {
        console.error(err);
        toast({
          variant: "destructive",
          title: "เกิดข้อผิดพลาด",
          description: err.message || "ไม่สามารถดาวน์โหลดไฟล์ได้",
        });
      } finally {
        setExporting(false);
      }
      return;
    }

    // โหมดรายงวด: ทำผ่าน Client ได้รวดเร็วเพราะข้อมูลแค่หลักร้อย-พันคน
    if (!filteredData.length) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 50));

    try {
      const rows = [
        [
          "ลำดับ",
          "User ID",
          "ชื่อ",
          "นามสกุล",
          "จำนวนสลากที่ซื้อ (ใบ)",
          "เบอร์โทรศัพท์ (66x)",
          "LINE ID",
          "ช่องทางการแจ้งเตือน",
        ],
        ...filteredData.map((u, idx) => [
          idx + 1,
          u.userId || "-",
          u.firstName || "-",
          u.lastName || "-",
          Number(u.size) || 0,
          formatPhoneForExport(u.phone),
          u.lineId || "-",
          u.notifyType || "-",
        ]),
      ];

      const wb = xlsx.utils.book_new();
      const ws = xlsx.utils.aoa_to_sheet(rows);

      const colWidths = [
        { wch: 8 },
        { wch: 28 },
        { wch: 18 },
        { wch: 18 },
        { wch: 22 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
      ];
      ws["!cols"] = colWidths;

      xlsx.utils.book_append_sheet(wb, ws, "รายชื่อผู้ซื้อ");
      const buffer = xlsx.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `รายชื่อผู้ซื้องวด_${label}_${notifyFilter}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      logReportDownload();
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      toast({
        title: "ดาวน์โหลดไฟล์ Excel สำเร็จ",
        description: `ดาวน์โหลดรายชื่อผู้ซื้อ ${filteredData.length.toLocaleString()} รายการเรียบร้อยแล้ว`,
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "ดาวน์โหลดไฟล์ไม่สำเร็จ",
        description: err.message,
      });
    } finally {
      setExporting(false);
    }
  }, [filteredData, label, isYear, year, notifyFilter, toast, callApi]);

  // Copy Phone Numbers (SMS or Current Filter)
  const handleCopyPhones = useCallback(
    (targetType = "sms") => {
      let targets = filteredData;
      if (targetType === "sms") {
        targets = targets.filter(
          (u) => String(u.notifyType || "").toUpperCase() === "SMS",
        );
      }

      const phones = targets
        .map((u) => u.phone)
        .filter(Boolean)
        .map((p) => {
          const clean = String(p).trim();
          return clean.startsWith("0") ? clean : `0${clean}`;
        });

      if (!phones.length) {
        toast({
          title: "ไม่พบเบอร์โทรศัพท์",
          description: "ไม่มีรายการเบอร์โทรศัพท์ที่ตรงตามเงื่อนไขที่เลือก",
        });
        return;
      }

      navigator.clipboard.writeText(phones.join("\n"));
      toast({
        title: "คัดลอกเบอร์โทรศัพท์สำเร็จ",
        description: `คัดลอกเบอร์โทรศัพท์จำนวน ${phones.length.toLocaleString()} เบอร์ลงคลิปบอร์ดแล้ว`,
      });
    },
    [filteredData, toast],
  );

  // Copy single text helper
  const handleCopySingle = (text, idx) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text).trim());
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6 max-w-[1600px] mx-auto w-full">
      {/* Header */}
      <Header
        header="รายชื่อผู้ซื้องวดนี้ทั้งหมด"
        subHeader="รายงานและตรวจสอบรายชื่อลูกค้าที่ซื้อสลากสำเร็จในแต่ละงวด พร้อมสถิติและการดาวน์โหลด"
        icon={<FiUsers className="text-xl" />}
      />

      {/* Filter and Control Card */}
      <div className="bg-white rounded-md shadow-xs border border-slate-200/80 p-4 sm:p-5 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
            <FiFilter className="text-rose-600 text-base" />
            <span>ตัวกรองและเงื่อนไขการค้นหา</span>
          </div>

          {/* Action buttons (Right) */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* โหมดรายงวด: แสดงปุ่มคัดลอกเบอร์และดาวน์โหลดเมื่อมีข้อมูล */}
            {!isYear && rawData.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => handleCopyPhones("sms")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded-md border border-slate-200 transition-all"
                  title="คัดลอกเบอร์โทรศัพท์เฉพาะผู้รับแจ้งเตือนทาง SMS"
                >
                  <FiCopy className="text-slate-500" />
                  <span>คัดลอกเบอร์ SMS ({stats.smsCount.toLocaleString()})</span>
                </button>

                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={exporting || !filteredData.length}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 rounded-md shadow-xs transition-all disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <>
                      <Spinner className="w-3.5 h-3.5 border-white text-white" />
                      <span>กำลังดาวน์โหลด Excel...</span>
                    </>
                  ) : (
                    <>
                      <RiFileExcel2Line className="text-sm" />
                      <span>ดาวน์โหลด Excel (.xlsx)</span>
                    </>
                  )}
                </button>
              </>
            )}

            {/* โหมดทั้งปี: แสดงปุ่มดาวน์โหลดทันที ไม่ต้องกดค้นหาก่อน */}
            {isYear && (
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={exporting || !year}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 rounded-md shadow-xs transition-all disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <>
                    <Spinner className="w-3.5 h-3.5 border-white text-white" />
                    <span>กำลังประมวลผลไฟล์ Excel ปี {year}...</span>
                  </>
                ) : (
                  <>
                    <RiFileExcel2Line className="text-sm" />
                    <span>ดาวน์โหลดผู้ซื้อทั้งปี {year} (.xlsx)</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Filter Inputs Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
          {/* Mode + Round Date / Year */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
              <FiCalendar className="text-slate-400" />
              <span>{isYear ? "ปี (ทุกงวดในปี)" : "งวดวันที่"}</span>
            </label>
            <div className="flex gap-2">
              <select
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value);
                  setRawData([]);
                  setHasSearched(false);
                }}
                className="text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-md py-2 px-2 focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all"
              >
                <option value="round">รายงวด</option>
                <option value="year">ทั้งปี</option>
              </select>
              {isYear ? (
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-md py-2 px-3 focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      ปี {y}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                  className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-md py-2 px-3 focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all"
                >
                  {configs?.roundDates ? (
                    [...configs.roundDates]
                      .sort()
                      .reverse()
                      .map((d) => (
                        <option key={d} value={d}>
                          งวด {d}
                        </option>
                      ))
                  ) : (
                    <option value={roundDate}>{roundDate || "ไม่มีข้อมูล"}</option>
                  )}
                </select>
              )}
            </div>
          </div>

          {/* Notify Type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
              <FiMessageSquare className="text-slate-400" />
              <span>ช่องทางแจ้งเตือน</span>
            </label>
            <select
              value={notifyFilter}
              onChange={(e) => setNotifyFilter(e.target.value)}
              className="w-full text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-md py-2 px-3 focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all"
            >
              <option value="all">ทั้งหมด (All Channels)</option>
              <option value="line">LINE Notify</option>
              <option value="sms">SMS</option>
            </select>
          </div>

          {/* Search Term */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
              <FiSearch className="text-slate-400" />
              <span>ค้นหาในรายการ</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="ชื่อ, เบอร์โทร, User ID, LINE ID"
                className="w-full text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-md py-2 pl-8 pr-3 focus:bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 transition-all placeholder:text-slate-400"
              />
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            </div>
          </div>

          {/* Search Button (สำหรับโหมดรายงวด) หรือ Action Button (โหมดทั้งปี) */}
          <div className="flex items-center gap-2">
            {isYear ? (
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={exporting || !year}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-bold rounded-md shadow-xs transition-all disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <>
                    <Spinner className="w-3.5 h-3.5 border-white text-white" />
                    <span>กำลังสร้างไฟล์ Excel ปี {year}...</span>
                  </>
                ) : (
                  <>
                    <RiFileExcel2Line className="text-xs" />
                    <span>ดาวน์โหลดรายงานปี {year} ทันที</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white text-xs font-bold rounded-md shadow-xs transition-all disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Spinner className="w-3.5 h-3.5 border-white text-white" />
                    <span>กำลังดึงข้อมูล...</span>
                  </>
                ) : (
                  <>
                    <FiRefreshCw className="text-xs" />
                    <span>ค้นหาข้อมูล</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* KPI Metric Summary Cards */}
      {rawData.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          {/* Total Buyers */}
          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-blue-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>ผู้ซื้อทั้งหมดในงวด</span>
              <FiUsers className="text-blue-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-slate-900">
                {stats.totalBuyers.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">
              คำสั่งซื้อสถานะสำเร็จ (APPROVE)
            </span>
          </div>

          {/* Total Tickets */}
          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-rose-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>จำนวนสลากรวม</span>
              <FiSmartphone className="text-rose-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-rose-600">
                {stats.totalTickets.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">ใบ</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">
              เฉลี่ย ~{stats.totalBuyers ? (stats.totalTickets / stats.totalBuyers).toFixed(1) : 0} ใบ/คน
            </span>
          </div>

          {/* SMS Count */}
          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-amber-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>แจ้งเตือนผ่าน SMS</span>
              <FiMessageSquare className="text-amber-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-amber-600">
                {stats.smsCount.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">
              สัดส่วน {stats.totalBuyers ? ((stats.smsCount / stats.totalBuyers) * 100).toFixed(1) : 0}% ของผู้ซื้อ
            </span>
          </div>

          {/* LINE Count */}
          <div className="bg-white rounded-md p-4 shadow-xs border border-slate-200/80 border-l-4 border-l-emerald-500 flex flex-col justify-between">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
              <span>แจ้งเตือนผ่าน LINE</span>
              <FiMessageSquare className="text-emerald-500 text-sm" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-mono text-2xl font-bold text-emerald-600">
                {stats.lineCount.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <span className="text-[11px] text-slate-400 mt-0.5">
              สัดส่วน {stats.totalBuyers ? ((stats.lineCount / stats.totalBuyers) * 100).toFixed(1) : 0}% ของผู้ซื้อ
            </span>
          </div>
        </div>
      )}

      {/* Main Table Section */}
      <div className="bg-white rounded-md shadow-xs border border-slate-200/80 overflow-hidden flex flex-col">
        {/* Table Header Bar */}
        <div className="p-3.5 sm:p-4 bg-slate-50/80 border-b border-slate-200/80 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-800">
              รายการผู้ซื้อ ({filteredData.length.toLocaleString()} รายการ)
            </span>
            {filteredData.length !== rawData.length && (
              <span className="text-[11px] font-semibold text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                กรองจากทั้งหมด {rawData.length.toLocaleString()} คน
              </span>
            )}
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>แสดงแถวต่อหน้า:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="text-xs font-semibold text-slate-800 bg-white border border-slate-200 rounded-md py-1 px-2 focus:outline-none"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center p-16 gap-3 text-slate-400">
            <Spinner className="w-8 h-8 border-rose-600 text-rose-600" />
            <p className="text-xs font-semibold text-slate-500">กำลังดึงข้อมูลรายชื่อผู้ซื้อ...</p>
          </div>
        ) : paginatedData.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <Table className="w-full text-xs">
              <TableHeader className="bg-slate-100/90 text-slate-700">
                <TableRow>
                  <TableHead className="w-14 text-center font-bold">#</TableHead>
                  <TableHead
                    onClick={() => handleSort("userId")}
                    className="cursor-pointer hover:text-rose-600 transition-colors select-none font-bold"
                  >
                    <div className="flex items-center gap-1">
                      <span>User ID</span>
                      {sortBy === "userId" &&
                        (sortOrder === "asc" ? (
                          <FiArrowUp className="text-rose-600 text-xs" />
                        ) : (
                          <FiArrowDown className="text-rose-600 text-xs" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort("name")}
                    className="cursor-pointer hover:text-rose-600 transition-colors select-none font-bold"
                  >
                    <div className="flex items-center gap-1">
                      <span>ชื่อ - นามสกุล</span>
                      {sortBy === "name" &&
                        (sortOrder === "asc" ? (
                          <FiArrowUp className="text-rose-600 text-xs" />
                        ) : (
                          <FiArrowDown className="text-rose-600 text-xs" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    onClick={() => handleSort("size")}
                    className="cursor-pointer hover:text-rose-600 transition-colors select-none text-right font-bold"
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>จำนวนสลาก (ใบ)</span>
                      {sortBy === "size" &&
                        (sortOrder === "asc" ? (
                          <FiArrowUp className="text-rose-600 text-xs" />
                        ) : (
                          <FiArrowDown className="text-rose-600 text-xs" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead className="font-bold">เบอร์โทรศัพท์</TableHead>
                  <TableHead className="font-bold">LINE ID</TableHead>
                  <TableHead className="text-center font-bold">แจ้งเตือน</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((user, index) => {
                  const itemIndex = (currentPage - 1) * pageSize + index + 1;
                  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-";
                  const notifyUpper = String(user.notifyType || "").toUpperCase();

                  return (
                    <TableRow
                      key={user.userId || index}
                      className="hover:bg-slate-50/80 transition-colors border-b border-slate-100"
                    >
                      <TableCell className="text-center font-mono text-slate-500 font-semibold">
                        {itemIndex}
                      </TableCell>

                      {/* User ID */}
                      <TableCell className="font-mono text-slate-700">
                        <div className="flex items-center gap-1.5">
                          <span title={user.userId}>{user.userId || "-"}</span>
                          {user.userId && (
                            <button
                              type="button"
                              onClick={() => handleCopySingle(user.userId, `uid-${itemIndex}`)}
                              className="text-slate-400 hover:text-slate-700 active:scale-95 transition-all"
                              title="คัดลอก User ID"
                            >
                              {copiedIndex === `uid-${itemIndex}` ? (
                                <FiCheck className="text-emerald-500 text-xs" />
                              ) : (
                                <FiCopy className="text-xs" />
                              )}
                            </button>
                          )}
                        </div>
                      </TableCell>

                      {/* Name */}
                      <TableCell className="font-medium text-slate-900">
                        {fullName}
                      </TableCell>

                      {/* Size */}
                      <TableCell className="text-right">
                        <span className="font-mono font-bold text-rose-600 text-sm bg-rose-50 px-2 py-0.5 rounded border border-rose-200/60">
                          {Number(user.size || 0).toLocaleString()}
                        </span>
                      </TableCell>

                      {/* Phone */}
                      <TableCell className="font-mono text-slate-700">
                        <div className="flex items-center gap-1.5">
                          <span>{formatPhoneDisplay(user.phone)}</span>
                          {user.phone && (
                            <button
                              type="button"
                              onClick={() => handleCopySingle(user.phone, `ph-${itemIndex}`)}
                              className="text-slate-400 hover:text-slate-700 active:scale-95 transition-all"
                              title="คัดลอกเบอร์โทร"
                            >
                              {copiedIndex === `ph-${itemIndex}` ? (
                                <FiCheck className="text-emerald-500 text-xs" />
                              ) : (
                                <FiCopy className="text-xs" />
                              )}
                            </button>
                          )}
                        </div>
                      </TableCell>

                      {/* LINE ID */}
                      <TableCell className="font-mono text-slate-600">
                        {user.lineId || "-"}
                      </TableCell>

                      {/* Notify Type */}
                      <TableCell className="text-center">
                        {notifyUpper === "LINE" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            LINE
                          </span>
                        ) : notifyUpper === "SMS" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                            SMS
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                            {user.notifyType || "-"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-14 gap-2 text-slate-400">
            <FiUsers className="text-3xl text-slate-300" />
            <p className="text-xs font-semibold text-slate-500">
              {hasSearched
                ? "ไม่พบข้อมูลผู้ซื้อที่ตรงกับเงื่อนไขการค้นหา"
                : "กรุณากด 'ค้นหาข้อมูล' เพื่อดูรายชื่อผู้ซื้อในงวดนี้"}
            </p>
          </div>
        )}

        {/* Table Footer Pagination */}
        {filteredData.length > 0 && (
          <div className="p-3 sm:p-4 bg-slate-50/80 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
            <div>
              <span>แสดงแถวที่ </span>
              <span className="font-semibold text-slate-900">
                {((currentPage - 1) * pageSize + 1).toLocaleString()}
              </span>
              <span> ถึง </span>
              <span className="font-semibold text-slate-900">
                {Math.min(currentPage * pageSize, filteredData.length).toLocaleString()}
              </span>
              <span> จากทั้งหมด </span>
              <span className="font-semibold text-slate-900">
                {filteredData.length.toLocaleString()}
              </span>
              <span> รายการ</span>
            </div>

            {/* Pagination Buttons */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-xs font-medium rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="หน้าแรก"
              >
                หน้าแรก
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1 rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="หน้าก่อนหน้า"
              >
                <FiChevronLeft className="text-sm" />
              </button>

              <span className="px-2 font-mono font-bold text-slate-800">
                หน้า {currentPage} / {totalPages}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1 rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="หน้าถัดไป"
              >
                <FiChevronRight className="text-sm" />
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs font-medium rounded border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="หน้าสุดท้าย"
              >
                หน้าสุดท้าย
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoundDateBuyerList;
